// components/CartPopover.jsx
import { useNavigate } from "react-router-dom";
import { Popover } from "@mui/material";
import { useCart, cartLineRefForUpdate } from "../context/CartContext";
import { DEFAULT_AGENT_TAX_PERCENT } from "../utils/agentBillingPayload";

function roundMoney(n) {
  return Math.round(parseFloat(String(n ?? 0)) * 100) / 100;
}

const formatPrice = (price) => {
  const num = typeof price === "string" ? parseFloat(price) : price;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

/**
 * PaperProps for a MUI Popover that hangs off the top bar. MUI keeps the
 * behaviour (anchoring, focus trap, Escape, click-away); this swaps its paper
 * for design-system material, the same as .ob-panel and .ob-modal. The mini
 * bag, the wishlist and the account menu (UserMenu) all build theirs here, so
 * the three read as one family and a surface change is one edit.
 *
 * `className` carries the panel's own layout rules (.ob-flyout, .ob-usermenu);
 * `sx` is for the odd property the paper itself must own, such as a max
 * height MUI would otherwise set.
 */
export function topbarPaperProps(className, sx = {}) {
  return {
    className,
    elevation: 0,
    sx: {
      bgcolor: "var(--ob-bg-elev)",
      backgroundImage: "none",
      borderRadius: "var(--ob-r-lg)",
      boxShadow: "var(--ob-shadow-lg)",
      overflow: "hidden",
      ...sx,
    },
  };
}

export const FLYOUT_PAPER_PROPS = topbarPaperProps("ob-flyout", {
  maxHeight: "min(620px, calc(100vh - var(--ob-topbar-h) - 24px))",
});

const Icon = ({ d, className = "w-3.5 h-3.5", strokeWidth = 1.8 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

const BAG = "M4 8h16l-1.3 12.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8ZM9 8V6a3 3 0 0 1 6 0v2";
const CLOSE = "M18 6 6 18M6 6l12 12";
const TRASH = "M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v5M14 11v5";

export default function CartPopover({ anchorEl, onClose }) {
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const totalItems = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0) * item.quantity,
    0
  );
  const tax = roundMoney((subtotal * DEFAULT_AGENT_TAX_PERCENT) / 100);
  const total = roundMoney(subtotal + tax);

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={FLYOUT_PAPER_PROPS}
    >
      <div className="ob-flyout-head">
        <div>
          <span className="ob-eyebrow">Shopping bag</span>
          <div className="ob-display ob-flyout-title">Your bag</div>
        </div>
        <div className="flex items-center gap-1.5">
          {totalItems > 0 && (
            <span className="ob-chip">
              {totalItems} item{totalItems === 1 ? "" : "s"}
            </span>
          )}
          <button
            type="button"
            className="ob-iconbtn"
            onClick={onClose}
            aria-label="Close bag"
          >
            <Icon d={CLOSE} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {cartItems.length === 0 ? (
        <div className="ob-empty">
          <div className="ob-empty-glyph">
            <Icon d={BAG} className="w-6 h-6" strokeWidth={1.6} />
          </div>
          <div className="ob-display text-[22px]" style={{ color: "var(--ob-ink)" }}>
            Your bag is empty
          </div>
          <p className="text-[12.5px] mt-1.5 max-w-[260px] mx-auto leading-relaxed">
            Add something you love, or ask Orbiee to do it for you.
          </p>
          <button
            type="button"
            onClick={() => go("/products")}
            className="ob-btn ob-btn-solid mt-5"
          >
            Continue shopping
          </button>
        </div>
      ) : (
        <>
          <div className="ob-flyout-body ob-scroll">
            {cartItems.map((item, idx) => {
              const qty = Number(item.quantity) || 1;
              const title = item.name || item.title || "";
              return (
                <div
                  key={item.cartLineUid ?? `${idx}-${item.id}-${item.size ?? ""}`}
                  className="ob-mini"
                >
                  <img
                    src={item.image_uri || item.image}
                    alt={title}
                    className="ob-mini-img"
                    loading="lazy"
                    decoding="async"
                    onClick={() => item.id && go(`/item/${item.id}`)}
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div
                        className="ob-mini-title"
                        title={title}
                        onClick={() => item.id && go(`/item/${item.id}`)}
                      >
                        {title}
                      </div>
                      <div className="ob-mini-price">
                        USD {formatPrice((parseFloat(item.price) || 0) * qty)}
                      </div>
                    </div>

                    <div className="ob-mini-meta">
                      {item.size ? `Size ${item.size}` : "One size"}
                      {qty > 1 && ` · USD ${formatPrice(item.price)} each`}
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                      <span className="ob-qty">
                        <button
                          type="button"
                          disabled={qty <= 1}
                          onClick={() =>
                            updateQuantity(cartLineRefForUpdate(item), Math.max(1, qty - 1))
                          }
                          aria-label="Decrease quantity"
                        >
                          <Icon d="M5 12h14" className="w-3 h-3" strokeWidth={2} />
                        </button>
                        <span>{qty}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(cartLineRefForUpdate(item), qty + 1)}
                          aria-label="Increase quantity"
                        >
                          <Icon d="M12 5v14M5 12h14" className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </span>

                      <button
                        type="button"
                        className="ob-miniact ob-miniact-danger"
                        onClick={() => removeFromCart(cartLineRefForUpdate(item))}
                        title="Remove"
                        aria-label={`Remove ${title} from bag`}
                      >
                        <Icon d={TRASH} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ob-flyout-foot">
            <div className="ob-flyout-line">
              <span>Subtotal</span>
              <b>USD {formatPrice(subtotal)}</b>
            </div>
            <div className="ob-flyout-line">
              <span>Tax ({DEFAULT_AGENT_TAX_PERCENT}%)</span>
              <b>USD {formatPrice(tax)}</b>
            </div>
            <div className="ob-flyout-total">
              <span>Total</span>
              <span>USD {formatPrice(total)}</span>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => go("/cart")}
                className="ob-btn ob-btn-ghost flex-1"
              >
                View bag
              </button>
              <button
                type="button"
                onClick={() => go("/checkout")}
                className="ob-btn ob-btn-solid flex-[1.4]"
              >
                Checkout
                <Icon d="m9 18 6-6-6-6" className="w-[14px] h-[14px]" strokeWidth={1.9} />
              </button>
            </div>
          </div>
        </>
      )}
    </Popover>
  );
}
